import type { JSX } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';
import './Layout.css';
import { useQueueAccess } from '../hooks/useQueueAccess.ts';

type NavItem = Readonly<{ path: string; label: string; icon: string; requiresFeature?: string }>;

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { path: '/', label: 'Home', icon: '⌂' },
  { path: '/send', label: 'Send', icon: '↑' },
  { path: '/request', label: 'Request', icon: '↓' },
  { path: '/scan', label: 'Scan', icon: '⎚' },
  { path: '/queue', label: 'Queue', icon: '☰', requiresFeature: 'queue' },
  { path: '/settings', label: 'Settings', icon: '⚙' },
] as const;

const isNavActive = (currentPath: string, itemPath: string): boolean =>
  itemPath === '/' ? currentPath === '/' : currentPath.startsWith(itemPath);

export const Layout = (): JSX.Element => {
  const location = useLocation();
  const { hasFeature } = useSubscription();
  // Queue tab: own queue subscription OR active employee of a business (works that business's queue)
  const queueAccess = useQueueAccess();
  const featureVisible = (feature: string): boolean =>
    feature === 'queue' ? queueAccess.allowed : hasFeature(feature);

  const visibleItems = NAV_ITEMS.filter(item =>
    !item.requiresFeature || featureVisible(item.requiresFeature),
  );

  return (
    <div className="layout">
      <header className="layout-header">
        <img src="/assets/logo.svg" alt="" className="layout-logo-img" aria-hidden="true" width="28" height="28" />
        <span className="layout-logo">ChainSale</span>
      </header>

      <main className="layout-main">
        <Outlet />
      </main>

      <nav className="layout-nav" aria-label="Main navigation">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={`nav-item ${isNavActive(location.pathname, item.path) ? 'nav-item--active' : ''}`}
            aria-label={item.label}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
